// Centralized Sentry initialization. Imported once at the very top of the app entrypoint.
// Uses environment variables so we don't hard‑code secrets in source.
//
// Expected (configure in your env or Vercel/Render/etc.):
//  VITE_SENTRY_DSN                  -> Sentry project DSN
//  VITE_SENTRY_TRACES_SAMPLE_RATE   -> (optional) float 0.0 - 1.0
//  VITE_SENTRY_PROFILES_SAMPLE_RATE -> (optional) float 0.0 - 1.0 (for future profiling if enabled)
//  VITE_SENTRY_REPLAYS_SESSION_RATE -> (optional) float 0.0 - 1.0
//  VITE_SENTRY_REPLAYS_ERROR_RATE   -> (optional) float 0.0 - 1.0
//  VITE_GIT_SHA                     -> (optional) release identifier
//  VITE_SENTRY_SEND_PII             -> (optional) 'true' | 'false'
//
// NOTE: Keep sampling high (even 1.0) only while validating locally; reduce in production.

import * as Sentry from '@sentry/browser'

// Pull from Vite env (always prefixed with VITE_ to be exposed to client bundle)
const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined

// If no DSN provided, skip initialization silently (local dev convenience)
if (dsn) {
  const parseRate = (value: unknown, fallback: number): number => {
    const n = typeof value === 'string' ? parseFloat(value) : typeof value === 'number' ? value : NaN
    return Number.isFinite(n) && n >= 0 && n <= 1 ? n : fallback
  }

  const tracesSampleRate = parseRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 0.4)
  const replaysSessionSampleRate = parseRate(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_RATE, 0.1)
  const replaysOnErrorSampleRate = parseRate(import.meta.env.VITE_SENTRY_REPLAYS_ERROR_RATE, 1.0)
  const sendDefaultPii = (import.meta.env.VITE_SENTRY_SEND_PII ?? 'true') === 'true'

  // Dynamically build trace propagation targets so distributed tracing headers
  // are only sent to your own API origins (avoids leaking headers to third parties).
  const apiBase = import.meta.env.VITE_API_BASE_URL as string | undefined
  const traceTargets: (string | RegExp)[] = ['localhost', /\/api\//]
  if (apiBase) {
    try {
      const u = new URL(apiBase)
      // Escape dots for regex
  const safeHost = u.host.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
  const hostRegex = new RegExp(`^https?://${safeHost}/`)
      // Avoid duplicates
      if (!traceTargets.some(t => t instanceof RegExp && t.toString() === hostRegex.toString())) {
        traceTargets.push(hostRegex)
      }
    } catch {
      // ignore invalid URL
    }
  }

  Sentry.init({
    dsn,
    // Release & environment help grouping; environment falls back to vite mode
    release: (import.meta.env.VITE_RELEASE as string | undefined) 
      || (import.meta.env.VITE_GIT_SHA as string | undefined)
      || undefined,
  environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) || import.meta.env.MODE,
    sendDefaultPii,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Replay captures user sessions (respect sampling below)
      Sentry.replayIntegration()
    ],
    tracesSampleRate,
  tracePropagationTargets: traceTargets,
    // Session Replay sampling
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    // Future: enable profiling by adding profilesSampleRate when needed
    // profilesSampleRate: parseRate(import.meta.env.VITE_SENTRY_PROFILES_SAMPLE_RATE, 0) // add when using @sentry/profiling-browser
    // BeforeSend hook example (uncomment to redact or filter events)
    // beforeSend(event) {
    //   return event
    // }
  })
}

export { Sentry }
